import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ShoppingBag, Home } from 'lucide-react';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [orderData, setOrderData] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const orderId = searchParams.get('order_id');
  const paymentMethod = searchParams.get('method');

  useEffect(() => {
    // If coming from PayPal with order_id, it's already successful
    if (paymentMethod === 'paypal' && orderId) {
      setStatus('success');
      setOrderData({ order_id: orderId });
      clearCart();
      return;
    }

    // For Stripe, check session_id
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          setOrderData(response.data);
          clearCart();
          return true;
        } else if (response.data.status === 'expired') {
          setStatus('error');
          return true;
        }
        
        return false;
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts >= 4) {
          setStatus('error');
          return true;
        }
        return false;
      }
    };

    const checkStatus = async () => {
      const isComplete = await pollPaymentStatus();
      if (!isComplete && attempts < 5) {
        setTimeout(() => {
          setAttempts(prev => prev + 1);
        }, 2000);
      }
    };

    checkStatus();
  }, [sessionId, orderId, paymentMethod, attempts, clearCart]);

  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-16">
        <Card className="max-w-lg mx-auto p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 mx-auto mb-6 text-black animate-spin" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">
                Verificando tu pago...
              </h1>
              <p className="text-gray-600">
                Por favor espera mientras confirmamos tu pago.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-green-500" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                ¡Pago Completado!
              </h1>
              <p className="text-gray-600 mb-6">
                Gracias por tu compra. Tu pedido ha sido procesado correctamente.
              </p>
              
              {orderData?.order_id && (
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <p className="text-sm text-gray-500">Número de pedido:</p>
                  <p className="font-mono font-bold text-black">
                    {orderData.order_id.slice(0, 8).toUpperCase()}
                  </p>
                </div>
              )}

              {paymentMethod === 'paypal' && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-700">
                    🅿️ Pago realizado con PayPal
                  </p>
                </div>
              )}

              <p className="text-sm text-gray-500 mb-6">
                Recibirás un email de confirmación con los detalles de tu pedido.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/')}
                  className="bg-black hover:opacity-90"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Volver a la Tienda
                </Button>
                <Button
                  onClick={() => navigate('/mujer')}
                  variant="outline"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Seguir Comprando
                </Button>
              </div>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                Error en el Pago
              </h1>
              <p className="text-gray-600 mb-6">
                No pudimos verificar tu pago. Por favor, intenta de nuevo o contacta con nosotros.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate('/checkout')}
                  className="bg-black hover:opacity-90"
                >
                  Intentar de Nuevo
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Volver al Inicio
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default CheckoutSuccessPage;
